import { cn } from '@/lib/utils';
import { SectionHeader } from '@/components/molecules/SectionHeader';

// page-specific (Fase 2 PR 9): **D10 — FI-11** step «Scelta cruciale
// post-10°» del flusso di salita di livello. Pixel-parity dal pattern
// mockup `.lvup-stat-card` (styles.css 1921-1985), adattato a stack
// verticale di card grandi (le 3 opzioni richiedono descrizioni lunghe).
//
// Mostrato SOLO quando il PG sta passando da L10 a L11 (manuale V1
// rr. 1824-1834). Il giocatore deve scegliere una delle 3 opzioni
// esclusive: l'Addio (ritiro), l'Apprendista (gioco un secondo PG come
// instancabile), l'Instancabile (continua col cap caratteristiche).
//
// Per `'addio'`, gli step successivi (stat, move, ecc.) sono **skippati**
// — il flow va direttamente a confirm (vedi `LevelUpFlowSteps`).
//
// **PR 9 cambio API**: i bottoni Indietro/Avanti sono delegati a
// `<StepNav>` esterno renderizzato da `LevelUpFlowPage`.

type PastTenChoice = 'addio' | 'apprendista' | 'instancabile';

interface LevelUpStepPastTenProps {
  selected: PastTenChoice | undefined;
  onSelect: (choice: PastTenChoice) => void;
}

interface OptionDescriptor {
  id: PastTenChoice;
  label: string;
  shortDescription: string;
  longDescription: string;
}

const PAST_TEN_OPTIONS: readonly OptionDescriptor[] = [
  {
    id: 'addio',
    label: "L'Addio",
    shortDescription: 'Il personaggio si ritira leggendario',
    longDescription:
      'Definisci con il GM i dettagli del glorioso addio. Il personaggio è al sicuro e non potrà più essere messo in pericolo. È tempo di creare un nuovo personaggio e vivere altre avventure!',
  },
  {
    id: 'apprendista',
    label: "L'Apprendista",
    shortDescription: 'Trasmetti la tua saggezza a un nuovo eroe',
    longDescription:
      'Giocherai sia il tuo personaggio "senior" (che continua a crescere come Instancabile), sia il suo apprendista (un secondo personaggio che gestirai in parallelo). La gestione del secondo personaggio sarà disponibile in una fase futura del builder.',
  },
  {
    id: 'instancabile',
    label: "L'Instancabile",
    shortDescription: 'Continua le tue avventure, con i limiti del cap',
    longDescription:
      'Continua a giocare il personaggio con 3 differenze: (1) le formule level-dipendenti (Mana, ecc.) considerano sempre un livello massimo di 10; (2) le Caratteristiche non aumentano più; (3) avrai accesso alle 5 mosse comuni multiclasse a catena (Apprendista → Adepto → Esperto → Maestro → Gran Maestro Multiclasse).',
  },
];

export function LevelUpStepPastTen({ selected, onSelect }: LevelUpStepPastTenProps) {
  return (
    <section>
      <SectionHeader
        title="Oltre il 10° livello: scelta cruciale"
        sub="Manuale V1 rr. 1824-1834. Devi scegliere il destino del tuo personaggio: ritirarti, prendere un apprendista o continuare come Instancabile."
      />
      <div className="mt-4 flex flex-col gap-[0.6rem]">
        {PAST_TEN_OPTIONS.map((opt) => (
          <PastTenOptionCard
            key={opt.id}
            option={opt}
            isSelected={selected === opt.id}
            onSelect={() => onSelect(opt.id)}
          />
        ))}
      </div>
    </section>
  );
}

interface PastTenOptionCardProps {
  option: OptionDescriptor;
  isSelected: boolean;
  onSelect: () => void;
}

function PastTenOptionCard({ option, isSelected, onSelect }: PastTenOptionCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      className={cn(
        'border-rule bg-paper-bright relative cursor-pointer border-[1.5px] px-5 py-[1rem] text-left transition-[border-color,background-color,transform] duration-150',
        'hover:border-ink hover:-translate-y-px',
        isSelected && 'border-ink bg-paper-soft',
      )}
    >
      {isSelected ? (
        <span
          className="bg-ink pointer-events-none absolute top-0 bottom-0 left-0 w-[4px]"
          aria-hidden
        />
      ) : null}
      <div className="font-display text-ink mb-[0.25rem] text-[1rem] font-bold tracking-[0.05em] uppercase">
        {option.label}
        {isSelected ? <span className="text-ink font-bold"> ✓</span> : null}
      </div>
      <div className="text-ink-soft font-serif text-[0.92rem] italic">
        {option.shortDescription}
      </div>
      <p className="text-ink mt-[0.6rem] font-serif text-[0.92rem] leading-relaxed">
        {option.longDescription}
      </p>
    </button>
  );
}
